import type { MouseEvent } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable, ProductEventDataList } from 'corgi-types';
import {
  background,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  marginBottom,
  marginTop,
  opacity,
  width,
  type BackgroundProps,
  type BorderColorProps,
  type BorderRadiusProps,
  type BorderStyleProps,
  type BorderWidthProps,
  type MarginBottomProps,
  type OpacityProps,
  type WidthProps,
} from 'styled-system';

import Carousel from 'common/components/Carousel';
import { Flex } from 'common/components/Flex';
import type { TransitionProps } from 'common/theme/system-utilities';
import { transition } from 'common/theme/system-utilities';
import type { ImageMarkerProps } from '../types';
import { HorizontalCard } from './HorizontalCard';

const PreviewContainer = styled(Flex)<
  MarginBottomProps & BackgroundProps & BorderRadiusProps & OpacityProps & TransitionProps
>`
  ${marginBottom};
  ${background};
  ${borderRadius};
  ${opacity};
  ${transition};
  ${marginTop};
`;

const previewContainerStyles = {
  mb: 'sp_8',
  background: 'white',
  opacity: 1,
  transition: 'opacity 0.2s ease',
  mt: 'sp_10',
};

const SLIDE_WIDTH = [225, 325, null, 382];
const SLIDE_GAP = [10, 10, null, null];

const SlideContainer = styled.section<
  WidthProps & BorderRadiusProps & BorderWidthProps & BorderColorProps & BorderStyleProps
>`
  ${width};
  ${borderRadius};
  ${borderWidth};
  ${borderColor};
  ${borderStyle}
`;

const slideContainerStyles = {
  borderWidth: 'small',
  borderRadius: 'medium',
  borderStyle: 'solid',
};

interface MobileCarouselProps {
  markers: ImageMarkerProps[];
  activeMarkerIndex: Nullable<number>;
  list?: ProductEventDataList;
  onCardClick: (index: number) => (e: MouseEvent) => void;
}

export const MobileCarousel = ({
  markers,
  activeMarkerIndex,
  list,
  onCardClick,
}: MobileCarouselProps) => (
  <PreviewContainer data-corgi-id="previewcontainer" {...previewContainerStyles}>
    <Carousel slideGap={SLIDE_GAP} activeIndex={activeMarkerIndex ?? 0}>
      {markers.map(({ article }, index) => (
        <SlideContainer
          key={`${index}-${article.sku}`}
          width={SLIDE_WIDTH}
          borderColor={
            index === activeMarkerIndex ? 'sys.neutral.border.active' : 'sys.neutral.border.weak'
          }
          onClick={onCardClick(index)}
          {...slideContainerStyles}
        >
          <HorizontalCard article={article} list={list} index={index} />
        </SlideContainer>
      ))}
    </Carousel>
  </PreviewContainer>
);
